/**
 * 项目物料页面 - 状态管理 Reducer
 *
 * 管理页面所有状态：项目列表、筛选参数、表格数据、分页、审核记录弹窗等
 */
import type { ProjectMaterialDTO, ProjectMaterialAuditRecordDTO } from '@/api/projectMaterialApi'

// ====================== 分页类型定义 ======================

interface PaginationOption {
  total: number
  current: number
  pagesize: number
}

// ====================== State 类型定义 ======================

interface State {
  /** 项目列表（下拉选择器数据源，由 MaterialApi.GET_PROJECT_LIST 返回后映射） */
  projectList: { projectCode: string; projectName: string }[]
  /** 当前选中的项目编码 */
  selectedProjectCode: string
  /** 筛选参数（保存当前搜索条件，翻页时携带） */
  searchParam: Record<string, any>
  /** 表格数据源 */
  dataSource: ProjectMaterialDTO[]
  /** 表格加载状态 */
  isTableLoading: boolean
  /** 分页信息 */
  pagination: PaginationOption
  /** 审核记录弹窗是否可见 */
  auditDrawerVisible: boolean
  /** 当前查看审核记录的物料信息 */
  currentAuditMaterial: ProjectMaterialDTO | null
  /** 审核记录列表 */
  auditRecords: ProjectMaterialAuditRecordDTO[]
  /** 审核记录加载状态 */
  isAuditLoading: boolean
  /** 审核记录分页 */
  auditPagination: PaginationOption
}

// ====================== Action 类型定义 ======================

/** 使用全局 CustomActionTypes 自动生成 action type 字符串字面量联合类型 */
type ActionType = CustomActionTypes<State>

interface Action {
  type: ActionType
  payload: any
}

// ====================== 初始状态 ======================

export const initialState: State = {
  projectList: [],
  selectedProjectCode: '',
  searchParam: {},
  dataSource: [],
  isTableLoading: false,
  pagination: {
    total: 0,
    current: 1,
    pagesize: 20,
  },
  auditDrawerVisible: false,
  currentAuditMaterial: null,
  auditRecords: [],
  isAuditLoading: false,
  auditPagination: {
    total: 0,
    current: 1,
    pagesize: 10,
  },
}

// ====================== Reducer 函数 ======================

export default function reducer(
  state: State = initialState,
  action: Action,
): State {
  const newstate = { ...state }
  switch (action.type) {
    case 'setProjectListAction':
      newstate.projectList = action.payload
      return newstate
    case 'setSelectedProjectCodeAction':
      newstate.selectedProjectCode = action.payload
      return newstate
    case 'setSearchParamAction':
      newstate.searchParam = action.payload
      return newstate
    case 'setDataSourceAction':
      newstate.dataSource = action.payload
      return newstate
    case 'setIsTableLoadingAction':
      newstate.isTableLoading = action.payload
      return newstate
    case 'setPaginationAction':
      newstate.pagination = action.payload
      return newstate
    case 'setAuditDrawerVisibleAction':
      newstate.auditDrawerVisible = action.payload
      return newstate
    case 'setCurrentAuditMaterialAction':
      newstate.currentAuditMaterial = action.payload
      return newstate
    case 'setAuditRecordsAction':
      newstate.auditRecords = action.payload
      return newstate
    case 'setIsAuditLoadingAction':
      newstate.isAuditLoading = action.payload
      return newstate
    case 'setAuditPaginationAction':
      newstate.auditPagination = action.payload
      return newstate
    case 'resetStateAction':
      return initialState
    default:
      return newstate
  }
}

// ====================== Action Creator 函数 ======================

/** 设置项目列表 */
export const setProjectListAction = (val: { projectCode: string; projectName: string }[]): Action => ({
  type: 'setProjectListAction',
  payload: val,
})

/** 设置当前选中的项目编码 */
export const setSelectedProjectCodeAction = (val: string): Action => ({
  type: 'setSelectedProjectCodeAction',
  payload: val,
})

/** 设置搜索参数（翻页时保留搜索条件） */
export const setSearchParamAction = (val: Record<string, any>): Action => ({
  type: 'setSearchParamAction',
  payload: val,
})

/** 设置表格数据 */
export const setDataSourceAction = (val: ProjectMaterialDTO[]): Action => ({
  type: 'setDataSourceAction',
  payload: val,
})

/** 设置表格加载状态 */
export const setIsTableLoadingAction = (val: boolean): Action => ({
  type: 'setIsTableLoadingAction',
  payload: val,
})

/** 设置分页参数 */
export const setPaginationAction = (val: PaginationOption): Action => ({
  type: 'setPaginationAction',
  payload: val,
})

/** 设置审核记录弹窗可见性 */
export const setAuditDrawerVisibleAction = (val: boolean): Action => ({
  type: 'setAuditDrawerVisibleAction',
  payload: val,
})

/** 设置当前查看审核记录的物料 */
export const setCurrentAuditMaterialAction = (
  val: ProjectMaterialDTO | null,
): Action => ({
  type: 'setCurrentAuditMaterialAction',
  payload: val,
})

/** 设置审核记录数据 */
export const setAuditRecordsAction = (val: ProjectMaterialAuditRecordDTO[]): Action => ({
  type: 'setAuditRecordsAction',
  payload: val,
})

/** 设置审核记录加载状态 */
export const setIsAuditLoadingAction = (val: boolean): Action => ({
  type: 'setIsAuditLoadingAction',
  payload: val,
})

/** 设置审核记录分页 */
export const setAuditPaginationAction = (val: PaginationOption): Action => ({
  type: 'setAuditPaginationAction',
  payload: val,
})

/** 重置所有状态为初始值 */
export const resetStateAction = (): Action => ({
  type: 'resetStateAction',
  payload: undefined,
})
/**
 * 项目物料页面 - 状态管理 Reducer
 *
 * 管理页面所有状态：项目列表、筛选参数、表格数据、分页、审核记录弹窗等
 */
import type { ProjectMaterialDTO, ProjectMaterialAuditRecordDTO } from '@/api/projectMaterialApi'

// ====================== 分页类型定义 ======================

interface PaginationOption {
  total: number
  current: number
  pagesize: number
}

// ====================== State 类型定义 ======================

interface State {
  /** 项目列表（下拉选择器数据源，由 MaterialApi.GET_PROJECT_LIST 返回后映射） */
  projectList: { projectCode: string; projectName: string }[]
  /** 当前选中的项目编码 */
  selectedProjectCode: string
  /** 筛选参数（保存当前搜索条件，翻页时携带） */
  searchParam: Record<string, any>
  /** 表格数据源 */
  dataSource: ProjectMaterialDTO[]
  /** 表格加载状态 */
  isTableLoading: boolean
  /** 分页信息 */
  pagination: PaginationOption
  /** 审核记录弹窗是否可见 */
  auditDrawerVisible: boolean
  /** 当前查看审核记录的物料信息 */
  currentAuditMaterial: ProjectMaterialDTO | null
  /** 审核记录列表 */
  auditRecords: ProjectMaterialAuditRecordDTO[]
  /** 审核记录加载状态 */
  isAuditLoading: boolean
  /** 审核记录分页 */
  auditPagination: PaginationOption
}

// ====================== Action 类型定义 ======================

/** 使用全局 CustomActionTypes 自动生成 action type 字符串字面量联合类型 */
type ActionType = CustomActionTypes<State>

interface Action {
  type: ActionType
  payload: any
}

// ====================== 初始状态 ======================

export const initialState: State = {
  projectList: [],
  selectedProjectCode: '',
  searchParam: {},
  dataSource: [],
  isTableLoading: false,
  pagination: {
    total: 0,
    current: 1,
    pagesize: 20,
  },
  auditDrawerVisible: false,
  currentAuditMaterial: null,
  auditRecords: [],
  isAuditLoading: false,
  auditPagination: {
    total: 0,
    current: 1,
    pagesize: 10,
  },
}

// ====================== Reducer 函数 ======================

export default function reducer(
  state: State = initialState,
  action: Action,
): State {
  const newstate = { ...state }
  switch (action.type) {
    case 'setProjectListAction':
      newstate.projectList = action.payload
      return newstate
    case 'setSelectedProjectCodeAction':
      newstate.selectedProjectCode = action.payload
      return newstate
    case 'setSearchParamAction':
      newstate.searchParam = action.payload
      return newstate
    case 'setDataSourceAction':
      newstate.dataSource = action.payload
      return newstate
    case 'setIsTableLoadingAction':
      newstate.isTableLoading = action.payload
      return newstate
    case 'setPaginationAction':
      newstate.pagination = action.payload
      return newstate
    case 'setAuditDrawerVisibleAction':
      newstate.auditDrawerVisible = action.payload
      return newstate
    case 'setCurrentAuditMaterialAction':
      newstate.currentAuditMaterial = action.payload
      return newstate
    case 'setAuditRecordsAction':
      newstate.auditRecords = action.payload
      return newstate
    case 'setIsAuditLoadingAction':
      newstate.isAuditLoading = action.payload
      return newstate
    case 'setAuditPaginationAction':
      newstate.auditPagination = action.payload
      return newstate
    case 'resetStateAction':
      return initialState
    default:
      return newstate
  }
}

// ====================== Action Creator 函数 ======================

/** 设置项目列表 */
export const setProjectListAction = (val: { projectCode: string; projectName: string }[]): Action => ({
  type: 'setProjectListAction',
  payload: val,
})

/** 设置当前选中的项目编码 */
export const setSelectedProjectCodeAction = (val: string): Action => ({
  type: 'setSelectedProjectCodeAction',
  payload: val,
})

/** 设置搜索参数（翻页时保留搜索条件） */
export const setSearchParamAction = (val: Record<string, any>): Action => ({
  type: 'setSearchParamAction',
  payload: val,
})

/** 设置表格数据 */
export const setDataSourceAction = (val: ProjectMaterialDTO[]): Action => ({
  type: 'setDataSourceAction',
  payload: val,
})

/** 设置表格加载状态 */
export const setIsTableLoadingAction = (val: boolean): Action => ({
  type: 'setIsTableLoadingAction',
  payload: val,
})

/** 设置分页参数 */
export const setPaginationAction = (val: PaginationOption): Action => ({
  type: 'setPaginationAction',
  payload: val,
})

/** 设置审核记录弹窗可见性 */
export const setAuditDrawerVisibleAction = (val: boolean): Action => ({
  type: 'setAuditDrawerVisibleAction',
  payload: val,
})

/** 设置当前查看审核记录的物料 */
export const setCurrentAuditMaterialAction = (
  val: ProjectMaterialDTO | null,
): Action => ({
  type: 'setCurrentAuditMaterialAction',
  payload: val,
})

/** 设置审核记录数据 */
export const setAuditRecordsAction = (val: ProjectMaterialAuditRecordDTO[]): Action => ({
  type: 'setAuditRecordsAction',
  payload: val,
})

/** 设置审核记录加载状态 */
export const setIsAuditLoadingAction = (val: boolean): Action => ({
  type: 'setIsAuditLoadingAction',
  payload: val,
})

/** 设置审核记录分页 */
export const setAuditPaginationAction = (val: PaginationOption): Action => ({
  type: 'setAuditPaginationAction',
  payload: val,
})

/** 重置所有状态为初始值 */
export const resetStateAction = (): Action => ({
  type: 'resetStateAction',
  payload: undefined,
})
